import { IVendorPriority } from './types';

// Base API URL for vendor priorities
const API_BASE_URL = __API_BASE_ + '/api/vendorpriorities';

/**
 * Service for handling vendor priority data operations
 */
export const vendorPriorityService = {
    /**
     * Get all vendor priorities
     */
    getVendorPriorities: async (): Promise<IVendorPriority[]> => {
        try {
            const response = await fetch(`${API_BASE_URL}`, {
                credentials: 'include', // Include cookies for authentication
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (!response.ok) {
                // Check if response is HTML (likely an error page)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    const text = await response.text();
                    throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}`);
                }
                throw new Error(`Error fetching vendor priorities: ${response.status} ${response.statusText}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Failed to fetch vendor priorities:', error);
            throw error;
        }
    },

    /**
     * Get vendor priorities filtered by vendor type and/or entity type
     */
    getVendorPrioritiesByType: async (
        vendorType?: number, 
        entityType?: number
    ): Promise<IVendorPriority[]> => {
        try {
            // Build query parameters
            const params = new URLSearchParams();
            
            if (vendorType !== undefined) {
                params.append('vendorType', vendorType.toString());
            }
            
            if (entityType !== undefined) {
                params.append('entityType', entityType.toString());
            }
            
            const url = `${API_BASE_URL}/filter?${params.toString()}`;
            const response = await fetch(url, {
                credentials: 'include', // Include cookies for authentication
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (!response.ok) {
                // Check if response is HTML (likely an error page)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    const text = await response.text();
                    throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}`);
                }
                throw new Error(`Error fetching filtered vendor priorities: ${response.status} ${response.statusText}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Failed to fetch filtered vendor priorities:', error);
            throw error;
        }
    },

    /**
     * Update a single vendor priority
     */
    updateVendorPriority: async (priority: IVendorPriority): Promise<IVendorPriority> => {
        try {
            const response = await fetch(`${API_BASE_URL}/${priority.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for authentication
                body: JSON.stringify(priority),
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error updating vendor priority: ${response.status} ${response.statusText} - ${errorText}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Failed to update vendor priority:', error);
            throw error;
        }
    },

    /**
     * Update multiple vendor priorities at once
     */
    updateVendorPriorityBatch: async (priorities: IVendorPriority[]): Promise<IVendorPriority[]> => {
        try {
            const response = await fetch(`${API_BASE_URL}/batch`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for authentication
                body: JSON.stringify(priorities),
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error updating vendor priorities batch: ${response.status} ${response.statusText} - ${errorText}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Failed to update vendor priorities batch:', error);
            throw error;
        }
    }
};

export default vendorPriorityService;